/**
 * @fileoverview The Plant Doctor's answer view: what the window shows once an answer is in.
 *
 * Moved word for word out of PlantDoctor (cleanup C40). The likely problems
 * (ProblemBlock), Grow's own suggestion, "we need a photo of your plant" or
 * "nothing known", as the server's closing line says (B75), the extension
 * office line, the follow-up questions when any wait, the refusals and
 * notices, the old-photo line, the folded summary, Save to notes, Start over
 * and the photos-looked line. The window (PlantDoctor) holds the state and
 * the requests; this draws the answer and calls back.
 *
 * Where: PlantDoctor (the window), when an answer is shown and the server is
 * not holding it back for questions.
 * Words (en/es): plants.diagnose.* (updated, final, mostLikely, alsoPossible,
 * part*, moreAbout, evidenceHeading, reason*, ownHeading, ownBody, nothingKnown,
 * nothingKnownBody, wrongPhotoHeading, wrongPhotoBody, otherPlantPhoto, staleNote, summaryHeading, seeing,
 * saveNote, savedAlready, startOver, photosLooked, caveat).
 * Save to notes waits as the shared Button's loading (sitewide.saving); it gets no Cancel (B111: the dialog's X ends the conversation).
 *
 * @module DoctorAnswerView
 */

import React from 'react';
import { ChevronDown, FlaskConical } from 'lucide-react';
import { AiNearNote } from '@/features/billing/components/AiNearNote';
import { AiLimitNotice } from '@/features/billing/components/AiLimitNotice';
import type { AiLimitRefusal } from '@/features/billing/lib/aiLimit';
import { FailureText } from '@/shared/components/ServerFailure';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { PlantDiagnosis } from '@/shared/api/diagnose';
import { cn } from '@/lib/utils';
import { QUIET, FOLD_ROW } from './doctorStyles';
import { seeingAddsSomething, shortPhotoDay, extensionText } from './doctorText';
import { ProblemBlock, type AnswerLabels, type WhyLabels } from './AnswerBlocks';
import { FollowUp } from './FollowUp';

/** What DoctorAnswerView is given. */
interface DoctorAnswerViewProps {
  /** The answer shown. */
  answer: PlantDiagnosis;
  /** The plant's name, for the waiting steps and "We need a photo of your …". */
  plantName: string;
  /** Questions are waiting: "Update the answer" is the one primary action, and Save to notes steps back. */
  questionsWaiting: boolean;
  /** A round of taps is on its way. */
  narrowing: boolean;
  /** True once the updated answer has arrived: the waiting steps finish. */
  arrived: boolean;
  /** Called when the waiting steps have finished after the answer arrived. */
  onProgressDone: () => void;
  /** Sends one round's taps. */
  onNarrow: (answers: Array<{ id: string; answer: string }>) => void;
  /** The failure sentence of the last request, if any. */
  error: string | null;
  /** The photo help's allowance stopped the last request, if it did. */
  limitHit: AiLimitRefusal | null;
  /** The subscribe or switched-off line, when one applies (the window's). */
  accessNotice: React.ReactNode;
  /** The newest photo is too old for the forecast: the answer says so. */
  showStaleNote: boolean;
  /** When the newest photo was taken. */
  newestPhotoOn: string | null;
  /** Save to notes is on its way. */
  saving: boolean;
  /** The answer is already a note. */
  saved: boolean;
  /** Saves the answer as one of the plant's notes. */
  onSave: () => void;
  /** Back to the question, keeping what they wrote. */
  onStartOver: () => void;
}

/**
 * The answer the window shows once it is in.
 *
 * @param props - DoctorAnswerViewProps.
 */
export const DoctorAnswerView: React.FC<DoctorAnswerViewProps> = ({
  answer, plantName, questionsWaiting, narrowing, arrived, onProgressDone, onNarrow, error, limitHit, accessNotice,
  showStaleNote, newestPhotoOn, saving, saved, onSave, onStartOver,
}) => {
  const { reactTranslate: t, i18n } = useTranslation();
  // In the newest photo shows only when it says something the summary does not.
  const showSeeing = seeingAddsSomething(answer.seeing, answer.summary);
  // The closing line is the server's (doctorClosing, B75), always sent (C118 F3: no worked-out fallback).
  const closing = answer.closing;

  /** The headings of the answer's six parts. */
  const answerLabels: AnswerLabels = {
    whatItIs: t('plants.diagnose.partWhatItIs'),
    why: t('plants.diagnose.partWhy'),
    doNow: t('plants.diagnose.partDoNow'),
    nextSeason: t('plants.diagnose.partNextSeason'),
    spreads: t('plants.diagnose.partSpreads'),
    stillEdible: t('plants.diagnose.partStillEdible'),
    more: t('plants.diagnose.moreAbout'),
  };

  /** The labels "Why we think so" is read with. */
  const whyLabels: WhyLabels = {
    heading: t('plants.diagnose.evidenceHeading'),
    youSaid: t('plants.diagnose.reasonYouSaid'),
    photos: t('plants.diagnose.reasonPhotos'),
    weather: t('plants.diagnose.reasonWeather'),
  };

  return (
    <div className="space-y-5">
      {answer.round > 0 && (
        <p className="text-sm font-medium">
          {t(answer.questions.length > 0 ? 'plants.diagnose.updated' : 'plants.diagnose.final')}
        </p>
      )}

      {closing === 'matches' && answer.matches.length > 0 && (
        <div className="space-y-5">
          {answer.matches.map((match, i) => (
            <ProblemBlock
              key={i}
              name={match.name}
              commonName={match.commonName}
              answer={match.answer}
              answerLabels={answerLabels}
              rank={t(i === 0 ? 'plants.diagnose.mostLikely' : 'plants.diagnose.alsoPossible')}
              first={i === 0}
              reasons={match.reasons}
              evidence={match.evidence}
              whyLabels={whyLabels}
            />
          ))}
        </div>
      )}
      {closing === 'own' && answer.own && (
        <div className="space-y-2 rounded-md border border-warning/40 bg-warning-tint p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-warning">{t('plants.diagnose.ownHeading')}</p>
          <p className="text-xs text-warning">{t('plants.diagnose.ownBody')}</p>
          <ProblemBlock
            name={answer.own.why}
            commonName={answer.own.commonName}
            answer={answer.own.answer}
            answerLabels={answerLabels}
            rank={null}
            first
            reasons={answer.own.reasons}
            evidence={answer.own.evidence}
            whyLabels={whyLabels}
          />
        </div>
      )}
      {closing === 'wrongPhoto' && (
        <div className="space-y-2">
          <p className="text-lg font-semibold font-body">{t('plants.diagnose.wrongPhotoHeading', { plant: plantName })}</p>
          <p className={cn('text-sm', QUIET)}>{t('plants.diagnose.wrongPhotoBody', { plant: plantName })}</p>
        </div>
      )}
      {closing === 'fine' && (
        <div className="space-y-2">
          <p className="text-lg font-semibold font-body">{t('plants.diagnose.nothingKnown')}</p>
          <p className={cn('text-sm', QUIET)}>{t('plants.diagnose.nothingKnownBody')}</p>
        </div>
      )}

      {answer.extension && (
        <p role="note" className="flex gap-2 rounded-md border bg-muted p-2.5 text-sm">
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>{extensionText(answer.extension, t)}</span>
        </p>
      )}
      {/* With "wrongPhoto" the closing line has already asked for the right photo. */}
      {answer.otherPlantPhoto && closing !== 'wrongPhoto' && <p className={cn('text-sm', QUIET)}>{t('plants.diagnose.otherPlantPhoto')}</p>}

      {questionsWaiting && (
        <FollowUp
          key={`${answer.checkupId}-${answer.round}`}
          questions={answer.questions}
          again={answer.round > 0}
          busy={narrowing}
          ready={arrived}
          onProgressDone={onProgressDone}
          plantName={plantName}
          onSend={onNarrow}
        />
      )}
      {error && <p role="alert" className="text-sm text-destructive"><FailureText text={error} /></p>}
      {limitHit && <AiLimitNotice refusal={limitHit} />}
      {accessNotice}
      {/* Most of a subscriber's photo help used: said once, quietly. */}
      {!limitHit && <AiNearNote />}

      {showStaleNote && newestPhotoOn && (
        <p role="note" className={cn('text-sm', QUIET)}>
          {t('plants.diagnose.staleNote', { date: shortPhotoDay(newestPhotoOn, i18n.language) })}
        </p>
      )}
      {(answer.summary || showSeeing) && (
        <details className="group rounded-md border">
          <summary className={FOLD_ROW}>
            {t('plants.diagnose.summaryHeading')}
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div className="space-y-2 px-3 pb-3 text-sm">
            {answer.summary && <p>{answer.summary}</p>}
            {showSeeing && answer.seeing && <p className={QUIET}>{t('plants.diagnose.seeing', { seeing: answer.seeing })}</p>}
          </div>
        </details>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Button
          variant={questionsWaiting ? 'outline' : 'default'}
          onClick={onSave}
          loading={saving}
          disabled={saved || narrowing}
          className="min-h-11"
        >
          {saved ? t('plants.diagnose.savedAlready') : t('plants.diagnose.saveNote')}
        </Button>
        <button
          type="button"
          disabled={narrowing}
          onClick={onStartOver}
          className={cn('min-h-11 text-sm font-medium underline underline-offset-4 hover:text-foreground disabled:opacity-60', QUIET)}
        >
          {t('plants.diagnose.startOver')}
        </button>
      </div>

      <p className={cn('border-t pt-3 text-xs', QUIET)}>
        {t('plants.diagnose.photosLooked', { looked: answer.photosLooked, total: answer.photosTotal })} {t('plants.diagnose.caveat')}
      </p>
    </div>
  );
};
